export const SECURITY_ROLES = ['OWNER', 'ADMIN', 'OPERATOR', 'VIEWER'] as const;

export type SecurityRole = (typeof SECURITY_ROLES)[number];

export const OPERATIONAL_PROFILES = [
  'GENERAL',
  'BACKUP_OPERATIONS',
  'THREECX_OPERATIONS',
  'ONBOARDING_OPERATIONS',
] as const;

export type OperationalProfile = (typeof OPERATIONAL_PROFILES)[number];

export const OPERATIONAL_MODULES = [
  'OVERVIEW',
  'CLIENTS',
  'BACKUPS',
  'SERVERS',
  'MICROSOFT_365',
  'THREECX',
  'ONBOARDING',
  'PROJECTS',
] as const;

export type OperationalModule = (typeof OPERATIONAL_MODULES)[number];

export interface OperationalProfileDefinition {
  profile: OperationalProfile;
  preferredLandingRoute: '/overview' | '/backups' | '/3cx' | '/onboarding';
  resolvedLandingRoute: '/overview' | '/backups';
  dashboardOrder: readonly OperationalModule[];
}

const GENERAL_ORDER = [
  'OVERVIEW',
  'CLIENTS',
  'BACKUPS',
  'SERVERS',
  'MICROSOFT_365',
  'THREECX',
  'ONBOARDING',
  'PROJECTS',
] as const satisfies readonly OperationalModule[];

const PROFILE_DEFINITIONS: Record<OperationalProfile, OperationalProfileDefinition> = {
  GENERAL: {
    profile: 'GENERAL',
    preferredLandingRoute: '/overview',
    resolvedLandingRoute: '/overview',
    dashboardOrder: GENERAL_ORDER,
  },
  BACKUP_OPERATIONS: {
    profile: 'BACKUP_OPERATIONS',
    preferredLandingRoute: '/backups',
    resolvedLandingRoute: '/backups',
    dashboardOrder: GENERAL_ORDER,
  },
  THREECX_OPERATIONS: {
    profile: 'THREECX_OPERATIONS',
    preferredLandingRoute: '/3cx',
    resolvedLandingRoute: '/overview',
    dashboardOrder: GENERAL_ORDER,
  },
  ONBOARDING_OPERATIONS: {
    profile: 'ONBOARDING_OPERATIONS',
    preferredLandingRoute: '/onboarding',
    resolvedLandingRoute: '/overview',
    dashboardOrder: GENERAL_ORDER,
  },
};

export function operationalProfile(value: unknown): OperationalProfile {
  return typeof value === 'string'
    && (OPERATIONAL_PROFILES as readonly string[]).includes(value)
    ? value as OperationalProfile
    : 'GENERAL';
}

export function profileDefinition(value: unknown): OperationalProfileDefinition {
  return PROFILE_DEFINITIONS[operationalProfile(value)];
}

export function primarySecurityRole(roles: readonly SecurityRole[]): SecurityRole | null {
  return SECURITY_ROLES.find((role) => roles.includes(role)) ?? null;
}
